import { safeStorageGet, safeStorageRemove, safeStorageSet } from '../../../utils/storage'

const PINS_KEY = 'anipin.preview.gallery.pins.v1'

function safeJsonParse(text) {
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

function now() {
  return Date.now()
}

export function formatBytes(bytes) {
  const n = Number(bytes)
  if (!Number.isFinite(n) || n <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  let v = n
  let i = 0
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i += 1
  }
  const digits = i === 0 ? 0 : v >= 10 ? 1 : 2
  return `${v.toFixed(digits)} ${units[i]}`
}

function normalizeStringArray(input) {
  const raw = Array.isArray(input) ? input : []
  const out = []
  const seen = new Set()
  for (const item of raw) {
    const t = String(item || '').trim()
    if (!t) continue
    const key = t.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(t)
  }
  return out
}

export function normalizePin(input) {
  const p = input && typeof input === 'object' ? input : {}
  const id = String(p.id || '').trim()
  if (!id) return null

  const owner = String(p.owner || '').trim()
  const title = String(p.title || '').trim()
  const tags = normalizeStringArray(p.tags)
  const visibility = p.visibility === 'public' ? 'public' : 'private'

  const media = p.media && typeof p.media === 'object' ? p.media : {}
  const kindRaw = String(media.kind || '').trim().toLowerCase()
  const kind = kindRaw === 'video' ? 'video' : 'image'
  const url = String(media.url || '').trim()
  const thumbnailUrl = String(media.thumbnailUrl || '').trim()
  const mime = String(media.mime || '').trim()
  const sizeBytes = Number(media.sizeBytes)
  const width = Number(media.width)
  const height = Number(media.height)
  const durationMs = Number(media.durationMs)

  const likes = normalizeStringArray(p.likes)

  return {
    id,
    owner,
    title: title || 'Untitled',
    tags,
    visibility,
    createdAt: Number(p.createdAt) || now(),
    updatedAt: Number(p.updatedAt) || now(),
    likes,
    media: {
      kind,
      url,
      thumbnailUrl,
      mime,
      sizeBytes: Number.isFinite(sizeBytes) ? sizeBytes : 0,
      width: Number.isFinite(width) ? width : 0,
      height: Number.isFinite(height) ? height : 0,
      durationMs: Number.isFinite(durationMs) ? durationMs : 0,
    },
  }
}

export function loadPins() {
  const raw = safeStorageGet(PINS_KEY)
  const parsed = safeJsonParse(raw)
  const list = Array.isArray(parsed) ? parsed : []
  return list.map(normalizePin).filter(Boolean)
}

export function savePins(pins) {
  const list = Array.isArray(pins) ? pins.map(normalizePin).filter(Boolean) : []
  safeStorageSet(PINS_KEY, JSON.stringify(list))
  return list
}

export function upsertPin(pins, nextPin) {
  const pin = normalizePin(nextPin)
  if (!pin) return pins
  const list = Array.isArray(pins) ? pins.slice() : []
  const idx = list.findIndex((p) => p?.id === pin.id)
  if (idx >= 0) list[idx] = { ...list[idx], ...pin, updatedAt: now() }
  else list.unshift({ ...pin, createdAt: now(), updatedAt: now() })
  return list
}

export function deletePin(pins, pinId) {
  const id = String(pinId || '').trim()
  const list = Array.isArray(pins) ? pins : []
  if (!id) return list
  return list.filter((p) => p?.id !== id)
}

export function toggleLike(pins, pinId, userId) {
  const id = String(pinId || '').trim()
  const user = String(userId || '').trim()
  if (!id || !user) return pins
  const list = Array.isArray(pins) ? pins.slice() : []
  const idx = list.findIndex((p) => p?.id === id)
  if (idx < 0) return pins
  const pin = normalizePin(list[idx])
  if (!pin) return pins
  const likes = new Set(pin.likes || [])
  if (likes.has(user)) likes.delete(user)
  else likes.add(user)
  list[idx] = { ...pin, likes: Array.from(likes), updatedAt: now() }
  return list
}

export function listTags(pins) {
  const list = Array.isArray(pins) ? pins : []
  const map = new Map()
  for (const p of list) {
    for (const t of p?.tags || []) {
      const key = String(t || '').trim().toLowerCase()
      if (!key) continue
      map.set(key, t)
    }
  }
  return Array.from(map.values()).sort((a, b) => a.localeCompare(b))
}

export function clearPins() {
  safeStorageRemove(PINS_KEY)
}

